import { baseApi } from '@app/baseApi';

import type {
  CreateTenantRequest,
  DPHData,
  InviteUsersRequest,
  NotificationFrequency,
  QuickAction,
  Tenant,
  UpdateDPHRequest,
} from '@/types';

import {
  transformCreateTenantRequest,
  transformCreateTenantResponse,
  transformGetNotificationFrequenciesResponse,
  transformGetTenantResponse,
  transformGetTenantsResponse,
  transformInviteTenantUsersRequest,
} from './tenants.mappers';

/**
 * Tenant API credentials for programmatic access
 */
export interface TenantCredentials {
  tykApiKey?: string;
  tykSecretKey?: string;
  ftpUser?: string;
  ftpPassword?: string;
}

export const tenantsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all tenants
    getTenants: builder.query<Tenant[], void>({
      query: () => ({ url: 'tenantlist', endpoint: 'main' }),
      transformResponse: transformGetTenantsResponse,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ tenantId }) => ({
                type: 'Tenant' as const,
                id: tenantId,
              })),
              { type: 'Tenant', id: 'LIST' },
            ]
          : [{ type: 'Tenant', id: 'LIST' }],
    }),

    // Get single tenant
    getTenant: builder.query<Tenant, string>({
      query: (tenantId) => ({ url: `tenantinfo/${tenantId}`, endpoint: 'main' }),
      transformResponse: transformGetTenantResponse,
      providesTags: (_result, _error, tenantId) => [{ type: 'Tenant', id: tenantId }],
    }),

    // Get Elevio email hash for user authentication
    getElevioEmailHash: builder.query<string, string>({
      query: (email) => ({
        url: `eleviohash?email=${encodeURIComponent(email)}`,
        endpoint: 'main',
      }),
      transformResponse: (response: string) => response, // Returns hash string directly
    }),

    // Get quick actions (recommended activities)
    getQuickActions: builder.query<QuickAction[], void>({
      query: () => ({
        url: 'recommendedactions/99',
        endpoint: 'main',
      }),
      transformResponse: (response: QuickAction[]) => response, // Returns array of quick actions
    }),

    // Create tenant
    createTenant: builder.mutation<Tenant, CreateTenantRequest>({
      query: (data) => ({
        url: 'newtenant',
        endpoint: 'main',
        method: 'POST',
        body: transformCreateTenantRequest(data),
      }),
      transformResponse: transformCreateTenantResponse,
      invalidatesTags: [{ type: 'Tenant', id: 'LIST' }],
    }),

    // Get tenant credentials
    getTenantCredentials: builder.query<TenantCredentials, string>({
      query: (appId) => ({ url: `tenantcreds/${appId}`, endpoint: 'main' }),
      transformResponse: (response: {
        TykApiKey?: string;
        TykSecretKey?: string;
        FtpUser?: string;
        FtpPassword?: string;
      }) => ({
        tykApiKey: response.TykApiKey,
        tykSecretKey: response.TykSecretKey,
        ftpUser: response.FtpUser,
        ftpPassword: response.FtpPassword,
      }),
    }),

    // Invite users to tenant
    inviteTenantUsers: builder.mutation<void, { tenantId: string; data: InviteUsersRequest }>({
      query: ({ tenantId, data }) => ({
        url: `tenants/${tenantId}/permissions`,
        endpoint: 'main',
        method: 'POST',
        body: transformInviteTenantUsersRequest(data),
      }),
      invalidatesTags: (_result, _error, { tenantId }) => [
        { type: 'User', id: `tenant-${tenantId}` },
      ],
    }),

    // `updateTenantUserAdmin` lives only in usersApi.ts (a duplicate here overrode it).

    // Get active DPH for tenant
    getActiveDPH: builder.query<DPHData, string>({
      query: (tenantId) => ({
        url: `tenants/${tenantId}/active-dph`,
        endpoint: 'main',
      }),
      // Backend returns camelCase directly - no transformation needed
      providesTags: (_result, _error, tenantId) => [
        { type: 'DPH', id: tenantId },
        { type: 'Tenant', id: tenantId },
      ],
    }),

    // Get max data processing time threshold
    getMaxDataProcTime: builder.query<{ threshold: string }, string>({
      query: (tenantId) => ({
        url: `tenants/${tenantId}/max-data-proc-time`,
        endpoint: 'main',
      }),
    }),

    // Update tenant DPH
    updateTenantDPH: builder.mutation<void, UpdateDPHRequest>({
      query: ({ tenantId, dphType, data }) => ({
        url: `tenants/${tenantId}/manage-tenant-dph?dph_type=${dphType}`,
        endpoint: 'main',
        method: 'PUT',
        body: data,
      }),
      // Invalidate DPH cache after update
      invalidatesTags: (_result, _error, { tenantId }) => [{ type: 'DPH', id: tenantId }],
    }),

    // Update max data processing time for tenant
    updateTenantMaxDataProcTime: builder.mutation<
      void,
      { tenantId: string; maxDataProcTime: string }
    >({
      query: ({ tenantId, maxDataProcTime }) => ({
        url: `tenants/${tenantId}/max-data-proc-time?maxDataProcTime=${maxDataProcTime}`,
        endpoint: 'main',
        method: 'PATCH',
      }),
    }),

    // Update max data processing time for app
    updateAppMaxDataProcTime: builder.mutation<
      void,
      { tenantId: string; appId: string; maxDataProcTime: string }
    >({
      query: ({ tenantId, appId, maxDataProcTime }) => ({
        url: `tenants/${tenantId}/apps/${appId}/max-data-proc-time?maxDataProcTime=${maxDataProcTime}`,
        endpoint: 'main',
        method: 'PATCH',
      }),
    }),

    // Get notification frequencies
    getNotificationFrequencies: builder.query<NotificationFrequency[], void>({
      query: () => ({ url: 'notification-frequencies', endpoint: 'main' }),
      transformResponse: transformGetNotificationFrequenciesResponse,
    }),

    // Get subscriber by key
    getSubscriberByKey: builder.query<unknown, string>({
      query: (key) => ({ url: `getsubscriberbykey/${key}`, endpoint: 'main' }),
    }),

    // Add new subscriber to tenant
    addSubscriberToTenant: builder.mutation<void, string>({
      query: (key) => ({
        url: `addnewsubscriberexistingusertotenant/${key}`,
        endpoint: 'main',
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'Tenant', id: 'LIST' }],
    }),

    // Get tenant features (feature flags)
    // GET /{tenantId}/features - Returns array of { key: string, ... }
    // Used to conditionally show/hide UI features (e.g., "lingkapis" for Lingk APIs)
    getFeatures: builder.query<Array<{ key: string }>, string>({
      query: (tenantId) => ({
        url: `${tenantId}/features`,
        endpoint: 'main',
      }),
      providesTags: (_result, _error, tenantId) => [{ type: 'Tenant', id: `features-${tenantId}` }],
    }),
  }),
});

export const {
  useGetTenantsQuery,
  useGetTenantQuery,
  useGetElevioEmailHashQuery,
  useGetQuickActionsQuery,
  useCreateTenantMutation,
  useGetTenantCredentialsQuery,
  useInviteTenantUsersMutation,
  useGetActiveDPHQuery,
  useGetMaxDataProcTimeQuery,
  useUpdateTenantDPHMutation,
  useUpdateTenantMaxDataProcTimeMutation,
  useUpdateAppMaxDataProcTimeMutation,
  useGetNotificationFrequenciesQuery,
  useGetSubscriberByKeyQuery,
  useAddSubscriberToTenantMutation,
  useGetFeaturesQuery,
} = tenantsApi;
